import type { ReactNode } from "react";
import { Link } from "react-router-dom";

// Breadcrumb + prev/next navigation now lives in the shared StepsNavigation/
// StepsFooter components (rendered once by PortfolioRiskConcentration.tsx,
// same as UnderwritingEngineV2's AppLayout.tsx) instead of being duplicated
// here per-page. These wrappers just keep every page's content in the same
// frame, so no page had to change its own JSX.
//
// That frame is full-bleed with a 24px gutter, matching Risk Based Pricing's
// `main className="p-6"`. It used to be `mx-auto max-w-7xl`, which left a wide
// empty margin either side on anything above 1280px while RBP filled the width —
// the two products read as different applications side by side.

export function StepShell({ children }: { step?: number; children: ReactNode }) {
  return (
    <div className="w-full px-6">
      {children}
    </div>
  );
}

export function ManageLimitsShell({
  children,
  backTo,
  backLabel,
}: {
  children: ReactNode;
  backTo?: string;
  backLabel?: string;
}) {
  // backTo/backLabel were computed by ManageLimitsPage and then discarded here, so the
  // `?from=` convention rendered nothing. Three existing callers pass it (from=monitor,
  // from=capacity) and Joints adds a fourth, so the round trip needs a way back.
  return (
    <div className="w-full px-6">
      {backTo && (
        // Athena Yellow throughout -- dimmed to 50% by default (the same disabled:opacity-50
        // a disabled button already uses, e.g. "View Scenario Capacity" while its result isn't
        // ready), full opacity on hover. The shared "back" link every Manage Limits/Manage
        // Capital/Manage Investors/Alerts Config/Scenario page uses.
        <Link
          to={backTo}
          className="mb-2 inline-block text-xs font-medium text-[color:var(--colorYellow)] opacity-50 transition-opacity duration-150 hover:opacity-100"
        >
          {backLabel ?? "← Back"}
        </Link>
      )}
      {children}
    </div>
  );
}
